import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import AuthenticationGuard from './components/AuthenticationGuard';
import AuthButton from './components/AuthButton';
import RouteErrorBoundary from './components/RouteErrorBoundary';
import GlobalTargetFinderModal from './components/GlobalTargetFinderModal';
import TargetingWorkspaceDrawer from './components/TargetingWorkspaceDrawer';
import useGlobalShortcuts from './hooks/useGlobalShortcuts';
import { isIbcsNavEnabled, IBCS_NAV_EVENT, IBCS_NAV_KEY } from './utils/ibcsNavSetting';
import { buildWeaponeeringEntityPath } from './utils/weaponeeringEntityPath';
import WeaponeeringAPI from './services/WeaponeeringAPIClient';
import DynamicEngagementLauncher from './components/amd/DynamicEngagementLauncher';
import { VIEWS } from './navViews';
import { isJiatfNavEnabled, JIATF_NAV_EVENT, JIATF_NAV_KEY } from './utils/jiatfNavSetting';
import { brandForPath, modeForPath, MODES } from './utils/appMode';

/**
 * TMPC NextGen - Main Application
 * Navigation between visualization modules
 */

// Lazy load components for better performance
const ScenarioRunner = lazy(() => import('./components/ScenarioRunner'));
const ApproachVectors = lazy(() => import('./components/ApproachVectors'));
const FlightProfile = lazy(() => import('./components/FlightProfile'));
const MissionPlanner = lazy(() => import('./components/MissionPlanner'));
const MissionObjectivesPlanner = lazy(() => import('./components/MissionObjectivesPlanner'));
const EnhancedMissionObjectivesPlanner = lazy(() => import('./components/EnhancedMissionObjectivesPlanner'));
const TheaterMissionPlanner = lazy(() => import('./components/TheaterMissionPlanner'));
const MissionPlanningEntry = lazy(() => import('./components/MissionPlanningEntry'));
const TargetByTargetMissionPlanner = lazy(() => import('./components/TargetByTargetMissionPlanner'));
const ServiceHealthDashboard = lazy(() => import('./components/ServiceHealthDashboard'));
const TheaterVisualization3D = lazy(() => import('./components/3DTheaterVisualization'));
const TheaterDemo3D = lazy(() => import('./components/3DTheaterDemo'));
const StrikeExecution = lazy(() => import('./components/StrikeExecution'));
const MissionBriefing = lazy(() => import('./components/MissionBriefing'));
const MissionAuthoringTool = lazy(() => import('./components/MissionAuthoringTool'));
const AARDashboard = lazy(() => import('./components/AARDashboard'));
const AAREntryForm = lazy(() => import('./components/AAREntryForm'));
const AARComparisonView = lazy(() => import('./components/AARComparisonView'));
const WeaponeeringWorkflow = lazy(() => import('./components/WeaponeeringWorkflow'));
const WeaponeeringReviewQueue = lazy(() => import('./components/WeaponeeringReviewQueue'));
const WeaponeeringKanbanBoard = lazy(() => import('./components/WeaponeeringKanbanBoard'));
const EntityDetailPage = lazy(() => import('./components/EntityDetailPage'));
const AnalyticsDashboard = lazy(() => import('./components/AnalyticsDashboard'));
const StrikeMonitorView = lazy(() => import('./components/StrikeMonitorView'));
const TheaterWindowEntry = lazy(() => import('./components/TheaterWindowEntry'));
const GanttWindowEntry = lazy(() => import('./components/GanttWindowEntry'));
const ISTARPage = lazy(() => import('./components/ISTARPage'));
const DeploymentPlans = lazy(() => import('./components/DeploymentPlans'));
const DynamicEngagement = lazy(() => import('./components/DynamicEngagement'));
const AdversaryDeploymentPlans = lazy(() => import('./components/adversary/AdversaryDeploymentPlans'));
const StrikeConsole = lazy(() => import('./components/strike/StrikeConsole'));
const StrikeTheaterWindowEntry = lazy(() => import('./components/strike/StrikeTheaterWindowEntry'));
const StrikeGanttWindowEntry = lazy(() => import('./components/strike/StrikeGanttWindowEntry'));

const COLORS = {
  background: '#070a0d',
  panel: '#0c1117',
  panelBorder: '#1a2533',
  primary: '#00d4ff',
  success: '#00ff88',
  warning: '#ffaa00',
  text: '#e6f0ff',
  textDim: '#5a7090',
};

// Loading component
const LoadingSpinner = () => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    background: COLORS.background,
    color: COLORS.primary,
    fontFamily: '"Share Tech Mono", monospace',
  }}>
    <div style={{
      width: '60px',
      height: '60px',
      border: `3px solid ${COLORS.panelBorder}`,
      borderTop: `3px solid ${COLORS.primary}`,
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
    }} />
    <div style={{ marginTop: '20px', letterSpacing: '2px' }}>LOADING MODULE...</div>
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [openDropdown, setOpenDropdown] = useState(null);
  const { user } = useAuth0();

  // Target Finder & Workspace state
  const [showTargetFinder, setShowTargetFinder] = useState(false);
  const [showWorkspace, setShowWorkspace] = useState(false);
  // IBCS / JIATF-401 → Dynamic Engagement chooser: { mode, plans } when >1 plan exists.
  const [dePicker, setDePicker] = useState(null);

  // Smart launch for a mode's Dynamic Engagement nav item, over that mode's plans
  // only (IBCS: AMD plans; JIATF-401: C-UAS plans): 0 accepted plans navigates
  // through to the "no plan" screen; exactly 1 launches it directly; more than 1
  // opens a chooser modal.
  const launchDynamicEngagement = async (mode) => {
    setOpenDropdown(null);
    try {
      const plans = await WeaponeeringAPI.listDeploymentPlans('accepted', mode.profile);
      if (!plans || plans.length === 0) { navigate(mode.engagementPath); return; }
      if (plans.length === 1) { navigate(mode.engagementPath, { state: { planId: plans[0].id, plan: plans[0], fromNav: true } }); return; }
      setDePicker({ mode, plans });
    } catch (err) {
      console.warn('Could not load deployment plans for launch:', err);
      navigate(mode.engagementPath);
    }
  };

  // Inbox opener — WeaponeeringWorkflow registers this so the Workspace Drawer can trigger it
  const openInboxRef = React.useRef(null);
  const handleOpenInbox = React.useCallback(() => {
    if (openInboxRef.current) openInboxRef.current();
  }, []);

  // Unread priority request count — drives the attention badge on the Weaponeering nav card
  const [weaponeeringUnread, setWeaponeeringUnread] = useState(0);
  useEffect(() => {
    if (!user?.email) return;
    const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
    const fetchCount = async () => {
      try {
        const token = await WeaponeeringAPI.resolveToken();
        const res = await fetch(`${API_BASE}/api/weaponeering/workspace/${encodeURIComponent(user.email)}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.ok) {
          const data = await res.json();
          setWeaponeeringUnread((data.priority_items || []).filter(i => !i.priority_acknowledged).length);
        }
      } catch { /* non-critical */ }
    };
    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    return () => clearInterval(interval);
  }, [user?.email]);

  // Global keyboard shortcuts
  useGlobalShortcuts({
    onOpenTargetFinder: () => setShowTargetFinder(true),
    onOpenWorkspace: () => setShowWorkspace(prev => !prev),
  });

  // IBCS nav visibility — toggled from Platform Management, reacts live.
  const [ibcsNavEnabled, setIbcsNavEnabledState] = useState(isIbcsNavEnabled());
  useEffect(() => {
    const sync = () => setIbcsNavEnabledState(isIbcsNavEnabled());
    const onStorage = (e) => { if (e.key === IBCS_NAV_KEY) sync(); };
    window.addEventListener(IBCS_NAV_EVENT, sync);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(IBCS_NAV_EVENT, sync);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  // JIATF-401 nav visibility — the same pattern as IBCS, its own switch.
  const [jiatfNavEnabled, setJiatfNavEnabledState] = useState(isJiatfNavEnabled());
  useEffect(() => {
    const sync = () => setJiatfNavEnabledState(isJiatfNavEnabled());
    const onStorage = (e) => { if (e.key === JIATF_NAV_KEY) sync(); };
    window.addEventListener(JIATF_NAV_EVENT, sync);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(JIATF_NAV_EVENT, sync);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  // Filter views - only show non-hidden primary items (and gate IBCS and JIATF-401 behind their settings)
  const visibleViews = VIEWS.filter(view => !view.hidden
    && (!view.requiresIbcsNav || ibcsNavEnabled) && (!view.requiresJiatfNav || jiatfNavEnabled));

  // The header brand follows the page: IBCS or JIATF-401 on their pages, TMPC NEXTGEN everywhere else
  const brand = brandForPath(location.pathname);
  const brandMode = modeForPath(location.pathname);

  // Get dev/experimental views for dropdown
  const devViews = VIEWS.filter(view => view.hidden);

  // Get current view based on URL path - check dropdown subitems too
  const currentView = VIEWS.find(view => {
    if (view.path === location.pathname) return true;
    if (view.subItems) {
      return view.subItems.some(sub => sub.path === location.pathname);
    }
    return false;
  });


  return (
    <AuthenticationGuard>
    <div style={{
      minHeight: '100vh',
      background: COLORS.background,
      color: COLORS.text,
      fontFamily: '"Rajdhani", sans-serif',
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@400;500;600;700&family=Share+Tech+Mono&display=swap');
        
        * { box-sizing: border-box; margin: 0; padding: 0; }
        
        .nav-btn {
          background: transparent;
          border: 1px solid ${COLORS.panelBorder};
          color: ${COLORS.textDim};
          padding: 10px 16px;
          font-family: 'Share Tech Mono', monospace;
          font-size: 11px;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .nav-btn:hover:not(:disabled) {
          border-color: ${COLORS.primary};
          color: ${COLORS.primary};
        }

        .nav-btn.active {
          background: ${COLORS.primary}20;
          border-color: ${COLORS.primary};
          color: ${COLORS.primary};
        }

        .nav-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
          color: ${COLORS.textDim};
        }

        /* Attention dot — on the trigger button */
        .nav-priority-dot {
          position: absolute;
          top: 4px;
          right: 4px;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #ef4444;
          box-shadow: 0 0 0 0 rgba(239,68,68,0.7);
          animation: nav-pulse 2s ease-in-out infinite;
        }

        /* Smaller inline dot inside the dropdown badge */
        .nav-priority-dot-sm {
          display: inline-block;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #f87171;
          box-shadow: 0 0 0 0 rgba(248,113,113,0.6);
          animation: nav-pulse 2s ease-in-out infinite;
          flex-shrink: 0;
        }

        @keyframes nav-pulse {
          0%   { box-shadow: 0 0 0 0 rgba(239,68,68,0.7); }
          60%  { box-shadow: 0 0 0 5px rgba(239,68,68,0); }
          100% { box-shadow: 0 0 0 0 rgba(239,68,68,0); }
        }

        .nav-tooltip-wrap {
          position: relative;
        }
        .nav-tooltip-wrap .nav-tooltip {
          pointer-events: none;
          position: absolute;
          top: calc(100% + 10px);
          left: 50%;
          transform: translateX(-50%);
          opacity: 0;
          transition: opacity 0.15s ease, transform 0.15s ease;
          transform-origin: top center;
          transform: translateX(-50%) translateY(-4px);
          z-index: 2000;
          white-space: pre;
        }
        .nav-tooltip-wrap:hover .nav-tooltip {
          opacity: 1;
          transform: translateX(-50%) translateY(0);
        }
        .nav-tooltip-arrow {
          width: 0; height: 0;
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-bottom: 6px solid #1a2533;
          margin: 0 auto;
        }
        .nav-tooltip-box {
          background: #0c1117;
          border: 1px solid #1a2533;
          border-radius: 8px;
          padding: 10px 14px;
          box-shadow: 0 12px 36px rgba(0,0,0,0.7), 0 0 0 1px rgba(0,212,255,0.08);
          min-width: 200px;
        }
        .nav-tooltip-acronym {
          font-family: 'Share Tech Mono', monospace;
          font-size: 11px;
          font-weight: 700;
          color: #00d4ff;
          letter-spacing: 0.04em;
          line-height: 1.6;
          margin-bottom: 7px;
          padding-bottom: 7px;
          border-bottom: 1px solid #1a2533;
          white-space: pre;
        }
        .nav-tooltip-body {
          font-family: 'Share Tech Mono', monospace;
          font-size: 10px;
          color: #5a7090;
          line-height: 1.65;
          letter-spacing: 0.02em;
          white-space: pre;
        }
      `}</style>

      {/* Navigation Header */}
      <nav style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        background: COLORS.panel,
        borderBottom: `1px solid ${COLORS.panelBorder}`,
        padding: '8px 16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}>
          <div style={{
            fontSize: '16px',
            fontWeight: 700,
            letterSpacing: '2px',
            color: COLORS.primary,
          }} data-testid="app-brand" title={brandMode ? brandMode.title : 'Tomahawk Mission Planning Cell'}>
            {brand}
          </div>
          <div style={{
            width: '1px',
            height: '24px',
            background: COLORS.panelBorder,
          }} />
          <div style={{ display: 'flex', gap: '4px' }}>
            {visibleViews.map(view => {
              // Handle dropdown menu
              if (view.category === 'dropdown') {
                const isActive = view.subItems?.some(sub => sub.path === location.pathname);
                const isOpen = openDropdown === view.id;

                return (
                  <div key={view.id} style={{ position: 'relative' }}>
                    <button
                      className={`nav-btn ${isActive ? 'active' : ''}`}
                      onClick={() => setOpenDropdown(isOpen ? null : view.id)}
                      title={view.description}
                      style={{ position: 'relative' }}
                    >
                      <span>{view.icon}</span>
                      <span>{view.name}</span>
                      <span style={{ fontSize: '10px', marginLeft: '2px' }}>
                        {isOpen ? '▲' : '▼'}
                      </span>
                      {/* Pulsing attention dot when Weaponeering has unread priority requests */}
                      {view.id === 'system-weaponeering' && weaponeeringUnread > 0 && (
                        <span className="nav-priority-dot" />
                      )}
                    </button>

                    {isOpen && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        marginTop: '6px',
                        background: '#111827',
                        border: '1px solid #374151',
                        borderRadius: '10px',
                        padding: '12px',
                        zIndex: 1001,
                        boxShadow: '0 12px 40px rgba(0,0,0,0.6)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '6px',
                        width: '320px',
                      }}>
                        {view.subItems.map(subItem => {
                          // Non-clickable section header inside a dropdown
                          // (e.g. "Strike Practice" under Strike Execution).
                          if (subItem.sectionHeader) {
                            return (
                              <div key={subItem.id} style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '6px 4px 0',
                              }}>
                                <span style={{
                                  fontSize: '10px',
                                  fontWeight: 700,
                                  letterSpacing: '1.4px',
                                  textTransform: 'uppercase',
                                  color: '#9ca3af',
                                  fontFamily: '"Share Tech Mono", monospace',
                                  whiteSpace: 'nowrap',
                                }}>
                                  {subItem.name}
                                </span>
                                <span style={{ flex: 1, height: '1px', background: 'rgba(55,65,81,0.8)' }} />
                              </div>
                            );
                          }
                          const isActivePath = location.pathname === subItem.path;
                          return (
                            <button
                              key={subItem.id}
                              onClick={() => {
                                if (subItem.id === 'dynamic-engagement') { launchDynamicEngagement(MODES.ibcs); }
                                else if (subItem.id === 'jiatf-dynamic-engagement') { launchDynamicEngagement(MODES.jiatf); }
                                else { navigate(subItem.path); setOpenDropdown(null); }
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '12px',
                                padding: '10px 12px',
                                background: isActivePath ? 'rgba(0,212,255,0.12)' : '#1f2937',
                                border: `1px solid ${isActivePath ? 'rgba(0,212,255,0.4)' : 'rgba(55,65,81,0.8)'}`,
                                borderRadius: '8px',
                                cursor: 'pointer',
                                textAlign: 'left',
                                transition: 'border-color 0.15s, background 0.15s',
                                width: '100%',
                              }}
                              onMouseEnter={e => {
                                if (!isActivePath) {
                                  e.currentTarget.style.background = '#283040';
                                  e.currentTarget.style.borderColor = 'rgba(0,212,255,0.35)';
                                }
                              }}
                              onMouseLeave={e => {
                                if (!isActivePath) {
                                  e.currentTarget.style.background = '#1f2937';
                                  e.currentTarget.style.borderColor = 'rgba(55,65,81,0.8)';
                                }
                              }}
                            >
                              <span style={{ fontSize: '22px', flexShrink: 0, lineHeight: 1, marginTop: '1px' }}>
                                {subItem.icon}
                              </span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{
                                  display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px',
                                }}>
                                  <span style={{
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    color: isActivePath ? '#67e8f9' : '#f3f4f6',
                                    fontFamily: '"Rajdhani", sans-serif',
                                    letterSpacing: '0.3px',
                                  }}>
                                    {subItem.name}
                                  </span>
                                  {/* Priority badge on Weaponeering card */}
                                  {subItem.id === 'weaponeering' && weaponeeringUnread > 0 && (
                                    <span style={{
                                      display: 'inline-flex', alignItems: 'center', gap: '4px',
                                      background: 'rgba(239,68,68,0.15)',
                                      border: '1px solid rgba(239,68,68,0.5)',
                                      borderRadius: '10px',
                                      padding: '1px 7px',
                                      fontSize: '10px',
                                      fontWeight: 700,
                                      color: '#f87171',
                                      fontFamily: '"Share Tech Mono", monospace',
                                      letterSpacing: '0.3px',
                                    }}>
                                      <span className="nav-priority-dot-sm" />
                                      {weaponeeringUnread} PRIORITY
                                    </span>
                                  )}
                                </div>
                                <div style={{
                                  fontSize: '11px',
                                  color: '#9ca3af',
                                  lineHeight: 1.4,
                                  fontFamily: '"Rajdhani", sans-serif',
                                }}>
                                  {subItem.description}
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              // Handle regular button
              const btn = (
                <button
                  key={view.id}
                  className={`nav-btn ${currentView?.id === view.id ? 'active' : ''}`}
                  onClick={() => !view.disabled && navigate(view.path)}
                  title={view.tooltip ? undefined : view.description}
                  disabled={view.disabled}
                  style={{ position: 'relative' }}
                >
                  {view.icon.startsWith('/') || view.icon.startsWith('./') ? (
                    <img
                      src={view.icon}
                      alt={view.name}
                      style={{ width: '16px', height: '16px' }}
                    />
                  ) : (
                    <span>{view.icon}</span>
                  )}
                  <span>{view.name}</span>
                  {view.badge && (
                    <span style={{
                      position: 'absolute',
                      top: '-6px',
                      right: '-6px',
                      background: '#9d4edd',
                      color: '#fff',
                      fontSize: '9px',
                      fontWeight: '700',
                      padding: '2px 5px',
                      borderRadius: '4px',
                      letterSpacing: '0.5px',
                      lineHeight: '1',
                      fontFamily: '"Share Tech Mono", monospace',
                    }}>
                      {view.badge}
                    </span>
                  )}
                </button>
              );

              if (view.tooltip) {
                return (
                  <div key={view.id} className="nav-tooltip-wrap">
                    {btn}
                    <div className="nav-tooltip">
                      <div className="nav-tooltip-arrow" />
                      <div className="nav-tooltip-box">
                        <div className="nav-tooltip-acronym">{view.tooltip.acronym}</div>
                        <div className="nav-tooltip-body">{view.tooltip.body}</div>
                      </div>
                    </div>
                  </div>
                );
              }

              return btn;
            })}
          </div>
        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          fontSize: '10px',
          fontFamily: '"Share Tech Mono", monospace',
          color: COLORS.textDim,
        }}>
          {/* Dev Tools Dropdown */}
          <div style={{ position: 'relative' }}>
            <button
              className="nav-btn"
              onClick={() => setOpenDropdown(openDropdown === 'devtools' ? null : 'devtools')}
              title="Developer tools and experimental views"
              style={{
                padding: '6px 10px',
                fontSize: '10px',
              }}
            >
              <span>🔧</span>
              <span>Dev Tools</span>
              <span style={{ fontSize: '8px', marginLeft: '2px' }}>
                {openDropdown === 'devtools' ? '▲' : '▼'}
              </span>
            </button>

            {openDropdown === 'devtools' && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: '6px',
                background: '#111827',
                border: '1px solid #374151',
                borderRadius: '10px',
                padding: '12px',
                zIndex: 1001,
                boxShadow: '0 12px 40px rgba(0,0,0,0.6)',
                display: 'flex',
                flexDirection: 'column',
                gap: '6px',
                width: '300px',
              }}>
                {devViews.map(view => {
                  const isActivePath = location.pathname === view.path;
                  const badgeLabel = view.category === 'experimental' ? '🧪 Experimental' : '🛠️ Dev';
                  return (
                    <button
                      key={view.id}
                      onClick={() => { navigate(view.path); setOpenDropdown(null); }}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                        padding: '10px 12px',
                        background: isActivePath ? 'rgba(0,212,255,0.12)' : '#1f2937',
                        border: `1px solid ${isActivePath ? 'rgba(0,212,255,0.4)' : 'rgba(55,65,81,0.8)'}`,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'border-color 0.15s, background 0.15s',
                        width: '100%',
                      }}
                      onMouseEnter={e => {
                        if (!isActivePath) {
                          e.currentTarget.style.background = '#283040';
                          e.currentTarget.style.borderColor = 'rgba(0,212,255,0.35)';
                        }
                      }}
                      onMouseLeave={e => {
                        if (!isActivePath) {
                          e.currentTarget.style.background = '#1f2937';
                          e.currentTarget.style.borderColor = 'rgba(55,65,81,0.8)';
                        }
                      }}
                    >
                      <span style={{ fontSize: '22px', flexShrink: 0, lineHeight: 1, marginTop: '1px' }}>
                        {view.icon}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                          <span style={{
                            fontSize: '13px',
                            fontWeight: 600,
                            color: isActivePath ? '#67e8f9' : '#f3f4f6',
                            fontFamily: '"Rajdhani", sans-serif',
                            letterSpacing: '0.3px',
                          }}>
                            {view.name}
                          </span>
                          <span style={{
                            fontSize: '9px',
                            padding: '1px 5px',
                            borderRadius: '3px',
                            background: view.category === 'experimental' ? 'rgba(139,92,246,0.2)' : 'rgba(55,65,81,0.6)',
                            color: view.category === 'experimental' ? '#a78bfa' : '#6b7280',
                            fontFamily: '"Share Tech Mono", monospace',
                            flexShrink: 0,
                          }}>
                            {badgeLabel}
                          </span>
                        </div>
                        <div style={{
                          fontSize: '11px',
                          color: '#9ca3af',
                          lineHeight: 1.4,
                          fontFamily: '"Rajdhani", sans-serif',
                        }}>
                          {view.description}
                        </div>
                      </div>
                    </button>
                  );
                })}

                {/* Repository link — always shown at bottom of Dev Tools */}
                <div style={{ marginTop: '2px', borderTop: '1px solid #374151', paddingTop: '8px' }}>
                  <a
                    href="https://github.com/peraton/tmpc-nextgen"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                      padding: '10px 12px',
                      background: '#1f2937',
                      border: '1px solid rgba(55,65,81,0.8)',
                      borderRadius: '8px',
                      textDecoration: 'none',
                      transition: 'border-color 0.15s, background 0.15s',
                      width: '100%',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.background = '#283040';
                      e.currentTarget.style.borderColor = 'rgba(0,212,255,0.35)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background = '#1f2937';
                      e.currentTarget.style.borderColor = 'rgba(55,65,81,0.8)';
                    }}
                  >
                    <span style={{ fontSize: '22px', flexShrink: 0, lineHeight: 1, marginTop: '1px' }}>📂</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                        <span style={{ fontSize: '13px', fontWeight: 600, color: '#f3f4f6', fontFamily: '"Rajdhani", sans-serif', letterSpacing: '0.3px' }}>
                          Repository
                        </span>
                        <span style={{ fontSize: '9px', padding: '1px 5px', borderRadius: '3px', background: 'rgba(55,65,81,0.6)', color: '#6b7280', fontFamily: '"Share Tech Mono", monospace' }}>
                          v1.0.0
                        </span>
                      </div>
                      <div style={{ fontSize: '11px', color: '#9ca3af', lineHeight: 1.4, fontFamily: '"Rajdhani", sans-serif' }}>
                        Source code on GitHub ↗
                      </div>
                    </div>
                  </a>
                </div>
              </div>
            )}
          </div>

          <div style={{
            width: '1px',
            height: '20px',
            background: COLORS.panelBorder,
          }} />

          {/* Authentication - rightmost position */}
          <AuthButton />
        </div>
      </nav>

      {/* Main Content */}
      <main style={{ paddingTop: '52px' }}>
        <RouteErrorBoundary label="App">
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              <Route path="/" element={<MissionPlanningEntry />} />
              <Route path="/mission-planning/detailed" element={
                <RouteErrorBoundary label="Target-by-Target Mission Planner">
                  <TargetByTargetMissionPlanner />
                </RouteErrorBoundary>
              } />
              <Route path="/mission-authoring" element={
                <RouteErrorBoundary label="Mission Authoring Tool">
                  <MissionAuthoringTool />
                </RouteErrorBoundary>
              } />
              <Route path="/weaponeering" element={
                <RouteErrorBoundary label="Weaponeering Workflow">
                  <WeaponeeringWorkflow
                    onOpenTargetFinder={() => setShowTargetFinder(true)}
                    onToggleWorkspace={() => setShowWorkspace(prev => !prev)}
                    isWorkspaceOpen={showWorkspace}
                    onRegisterOpenInbox={(fn) => { openInboxRef.current = fn; }}
                  />
                </RouteErrorBoundary>
              } />
              <Route path="/weaponeering/review-queue" element={<WeaponeeringReviewQueue />} />
              <Route path="/weaponeering/kanban" element={<WeaponeeringKanbanBoard standalone />} />
              <Route path="/weaponeering/sites/:entityId" element={<EntityDetailPage />} />
              <Route path="/weaponeering/facilities/:entityId" element={<EntityDetailPage />} />
              <Route path="/weaponeering/dmpis/:entityId" element={<EntityDetailPage />} />
              <Route path="/istar" element={<ISTARPage />} />
              <Route path="/strike-planning" element={
                <RouteErrorBoundary label="Theater Mission Planner">
                  <TheaterMissionPlanner />
                </RouteErrorBoundary>
              } />
              <Route path="/mission-briefing" element={<MissionBriefing />} />
              <Route path="/execution" element={
                <RouteErrorBoundary label="Strike Execution">
                  <StrikeExecution />
                </RouteErrorBoundary>
              } />
              <Route path="/execution/:planId" element={
                <RouteErrorBoundary label="Strike Execution">
                  <StrikeExecution />
                </RouteErrorBoundary>
              } />
              <Route path="/aar/dashboard" element={<AARDashboard />} />
              <Route path="/aar/entry/:missionId" element={<AAREntryForm />} />
              <Route path="/aar/view/:missionId" element={<AARComparisonView />} />
              <Route path="/platform-management" element={<ServiceHealthDashboard />} />
              <Route path="/analytics" element={
                <RouteErrorBoundary label="Analytics Dashboard">
                  <AnalyticsDashboard />
                </RouteErrorBoundary>
              } />
              <Route path="/3d-theater-demo" element={<TheaterDemo3D />} />
              <Route path="/3d-theater" element={
                <RouteErrorBoundary label="3D Theater Visualization">
                  <TheaterVisualization3D />
                </RouteErrorBoundary>
              } />
              <Route path="/strike-monitor" element={<StrikeMonitorView />} />
              <Route path="/strike-console" element={
                <RouteErrorBoundary label="Strike Console">
                  <StrikeConsole />
                </RouteErrorBoundary>
              } />
              <Route path="/strike-theater-window" element={<StrikeTheaterWindowEntry />} />
              <Route path="/strike-gantt-window" element={<StrikeGanttWindowEntry />} />
              <Route path="/deployment-plans" element={<DeploymentPlans mode={MODES.ibcs} />} />
              <Route path="/dynamic-engagement" element={<DynamicEngagement mode={MODES.ibcs} />} />
              <Route path="/jiatf-401/deployment-plans" element={<DeploymentPlans key="jiatf" mode={MODES.jiatf} />} />
              <Route path="/jiatf-401/dynamic-engagement" element={<DynamicEngagement key="jiatf" mode={MODES.jiatf} />} />
              <Route path="/adversary-deployment" element={<AdversaryDeploymentPlans />} />
              <Route path="/3d-theater-window" element={<TheaterWindowEntry />} />
              <Route path="/gantt-window" element={<GanttWindowEntry />} />
              <Route path="/mission-objectives" element={<MissionObjectivesPlanner />} />
              <Route path="/scenario" element={<ScenarioRunner />} />
              <Route path="/approach-vectors" element={<ApproachVectors />} />
              <Route path="/flight-profile" element={<FlightProfile />} />
              <Route path="/mission-planner" element={<MissionPlanner />} />
              <Route path="/objectives-legacy" element={<EnhancedMissionObjectivesPlanner />} />
            </Routes>
          </Suspense>
        </RouteErrorBoundary>
      </main>

      {/* IBCS / JIATF-401 → Dynamic Engagement plan chooser (shown only when >1 accepted plan) */}
      <DynamicEngagementLauncher
        plans={dePicker?.plans || null}
        eyebrow={dePicker?.mode === MODES.jiatf ? 'JIATF-401 · Counter-small-UAS' : 'Integrated Air & Missile Defense'}
        onClose={() => setDePicker(null)}
        onPick={(plan) => { const { mode } = dePicker; setDePicker(null); navigate(mode.engagementPath, { state: { planId: plan.id, plan, fromNav: true } }); }}
      />

      {/* Global Target Finder Modal */}
      <GlobalTargetFinderModal
        isOpen={showTargetFinder}
        onClose={() => setShowTargetFinder(false)}
        onSelectEntity={(entity) => {
          navigate(buildWeaponeeringEntityPath(entity));
          setShowTargetFinder(false);
        }}
      />

      {/* Targeting Workspace Drawer */}
      <TargetingWorkspaceDrawer
        isOpen={showWorkspace}
        onToggle={() => setShowWorkspace(prev => !prev)}
        analystId={user?.email}
        onOpenInbox={handleOpenInbox}
        onSelectEntity={(item) => {
          // Navigate to weaponeering entity detail page based on entity type
          let path;

          if (item.entity_type === 'site') {
            path = `/weaponeering/sites/${item.entity_id}`;
          } else if (item.entity_type === 'facility') {
            path = `/weaponeering/facilities/${item.entity_id}`;
          } else if (item.entity_type === 'dmpi') {
            path = `/weaponeering/dmpis/${item.entity_id}`;
          } else {
            // Fallback to main weaponeering page if entity type unknown
            path = '/weaponeering';
          }

          navigate(path);
          setShowWorkspace(false);
        }}
      />
    </div>
    </AuthenticationGuard>
  );
}
